import { useState } from 'react'
import { Anchor, Button, Group, Paper, Select, Stack, Text, TextInput, Title } from '@mantine/core'
import { fuelSourceLabels, isSheetUrl, knownFuelSheet } from '@racedash/shared'
import type { EventFuel, FuelSource } from '@racedash/shared'
import { setEventFuel } from '../data/catalog'
import type { Event } from '../data/catalog'

/**
 * Where the venue reports refuelling, if anywhere.
 *
 * Its own card and saved as soon as it is chosen, apart from the event form:
 * it should not wait on — or be lost with — unsaved edits to the event's
 * details. A sheet also needs saying which, so choosing one saves the sheet
 * used before, and the link and car type can then be changed and saved here.
 */
export default function FuelSourceCard({ event }: { event: Event }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const value = event.fuel?.source ?? 'none'

  async function save(fuel: EventFuel | null) {
    setBusy(true)
    setError(null)
    try {
      await setEventFuel(event.id, fuel)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Paper withBorder p="lg">
      <Title order={2} size="h5" mb="xs">
        Fuel
      </Title>
      <Text c="dimmed" size="sm" mb="md">
        Where the circuit&apos;s fuel station publishes fills. With a source set, fuel is captured
        alongside timing and each fill is matched to its pit stop.
      </Text>
      <Stack gap="md">
        <Group align="flex-end">
          <Select
            label="Fuel reported via"
            w={320}
            value={value}
            disabled={busy}
            allowDeselect={false}
            data={[
              { value: 'none', label: 'Not reported' },
              ...Object.entries(fuelSourceLabels).map(([v, label]) => ({ value: v, label })),
            ]}
            onChange={(v) => {
              if (!v || v === value) return
              if (v === 'none') return save(null)
              const source = v as FuelSource
              return save(source === 'sheet' ? { source, sheetUrl: knownFuelSheet, sheetKind: 'FC' } : { source })
            }}
            error={value !== 'sheet' ? (error ?? undefined) : undefined}
          />
        </Group>
        {event.fuel?.source === 'sheet' && (
          // Keyed by what is saved, so a save elsewhere resets the fields.
          <SheetFields
            key={`${event.fuel.sheetUrl}|${event.fuel.sheetKind}`}
            fuel={event.fuel}
            busy={busy}
            error={error}
            onSave={save}
          />
        )}
      </Stack>
    </Paper>
  )
}

function SheetFields({
  fuel,
  busy,
  error,
  onSave,
}: {
  fuel: EventFuel
  busy: boolean
  error: string | null
  onSave: (fuel: EventFuel) => void
}) {
  const [url, setUrl] = useState(fuel.sheetUrl ?? '')
  const [kind, setKind] = useState(fuel.sheetKind ?? 'FC')
  const urlError = url.trim() && !isSheetUrl(url) ? 'Not a Google Sheets link' : null
  const dirty = url.trim() !== (fuel.sheetUrl ?? '') || kind.trim() !== (fuel.sheetKind ?? 'FC')

  return (
    <Stack gap="xs">
      <TextInput
        label="Sheet"
        description={
          <>
            Any link to it. It must be shared with anyone who has the link.{' '}
            {url.trim() !== knownFuelSheet && (
              <Anchor size="xs" component="button" type="button" onClick={() => setUrl(knownFuelSheet)}>
                Use the sheet from Charade and Portimão
              </Anchor>
            )}
          </>
        }
        placeholder="https://docs.google.com/spreadsheets/d/…"
        value={url}
        disabled={busy}
        onChange={(e) => setUrl(e.currentTarget.value)}
        error={urlError}
      />
      <Group align="flex-end">
        <TextInput
          label="Car type"
          description="Rows whose Type voiture is this"
          w={200}
          value={kind}
          disabled={busy}
          onChange={(e) => setKind(e.currentTarget.value)}
        />
        {/* Choosing the source saved it already; this saves edits to the
            link or type, and says so when there are none. */}
        <Button
          variant={dirty ? 'filled' : 'default'}
          disabled={!dirty || !url.trim() || !!urlError || !kind.trim()}
          loading={busy}
          onClick={() => onSave({ source: 'sheet', sheetUrl: url.trim(), sheetKind: kind.trim() })}
        >
          {dirty ? 'Save changes' : 'Saved'}
        </Button>
      </Group>
      {fuel.sheetUrl && (
        <Anchor href={fuel.sheetUrl} target="_blank" rel="noreferrer" size="sm">
          Open the sheet
        </Anchor>
      )}
      {error && (
        <Text c="red" size="sm">
          {error}
        </Text>
      )}
    </Stack>
  )
}
